<template>
  <div class="kra-check-container">
    <div class="card">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Verifying KRA status...</p>
      </div>

      <div v-else-if="!checked" class="initial-state">
        <h2>KRA Status Verification</h2>
        <p class="description">
          Before proceeding with KYC, we'll check if your PAN is already verified in the KRA system.
        </p>
        <button @click="checkKRAStatus" :disabled="isCheckingKRA" class="primary-btn">
          {{ isCheckingKRA ? 'Checking...' : 'Check KRA Status' }}
        </button>
      </div>

      <div v-else-if="kraStatus" class="result-state">
        <div v-if="isVerified" class="verified">
          <div class="icon-check">✓</div>
          <h3>KRA Verified</h3>
          <p>Your PAN is already verified in the KRA system.</p>
          <div class="details">
            <div class="detail-item">
              <span class="label">PAN Number:</span>
              <span class="value">{{ kraStatus.panNumber }}</span>
            </div>
            <div class="detail-item">
              <span class="label">Name:</span>
              <span class="value">{{ kraStatus.name }}</span>
            </div>
            <div class="detail-item">
              <span class="label">Status:</span>
              <span class="value status-verified">{{ kraStatus.statusDescription }}</span>
            </div>
          </div>
          <button @click="proceed" :disabled="isCheckingKRA" class="primary-btn">
            Continue to Doc Signing
          </button>
        </div>

        <div v-else class="not-verified">
          <div class="icon-info">ⓘ</div>
          <h3>KRA Not Verified</h3>
          <p>Your PAN & Aadhaar needs to be verified through KYC process.</p>
          <div class="details">
            <div class="detail-item-horizontal">
              <span class="label">PAN Number:</span>
              <span class="value">{{ kraStatus.panNumber }}</span>
            </div>
            <div class="status-message-box">
              <div class="status-icon">ℹ️</div>
              <div class="status-content">
                <span class="status-label">Status Information:</span>
                <p class="status-text">{{ kraStatus.statusDescription }}</p>
              </div>
            </div>
          </div>
          <button @click="proceedToKYC" class="primary-btn" :disabled="isCheckingKRA">
            Proceed to Video KYC
          </button>
        </div>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="icon-error">✕</div>
        <h3>Verification Failed</h3>
        <p>{{ error }}</p>
        <button @click="checkKRAStatus" class="secondary-btn" :disabled="isCheckingKRA">
          Try Again
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from '@/store/user-store';

export default {
  name: "KRAStatusCheck",
  props: {
    panNumber: {
      type: String,
      required: true
    }
  },
  emits: ['verified', 'not-verified'],
  data() {
    return {
      loading: false,
      checked: false,
      kraStatus: null,
      error: null,
      isCheckingKRA: false // NEW: Prevent duplicate KRA checks
    };
  },
  computed: {
    isVerified() {
      // Only check status field
      // "Registered" means KYC registration done but verification pending - needs Digio KYC
      // "Validated" means KYC is complete - can skip Digio KYC
      return this.kraStatus?.status === "Validated";
    }
  },
  methods: {
    async checkKRAStatus() {
      // Prevent duplicate checks
      if (this.isCheckingKRA) {
        console.log('KRA check already in progress');
        return;
      }

      this.loading = true;
      this.error = null;
      this.kraStatus = null;
      this.isCheckingKRA = true;

      try {
        const store = useUserStore();
        console.log('Checking KYC status first for PAN:', this.panNumber);

        // Step 1: Check KYC status first
        const kycPayload = {
          pan: store.dashboardData?.pan || this.panNumber,
          customerIdentifier: store.dashboardData?.phoneNumber
        };

        const kycResponse = await store.getKYCRequestStatus(kycPayload);
        console.log('KYC status response:', kycResponse);

        // Correct path: response.data.Status (not response.data.data.Status)
        const kycStatus = kycResponse?.data?.Status;
        console.log('KYC Status:', kycStatus);

        // If KYC is Done or Verified, treat as validated
        if (kycStatus === 'Done' || kycStatus === 'Verified' || kycStatus === 'Completed') {
          console.log('KYC already verified, setting kraStatus to Validated');
          this.kraStatus = {
            panNumber: store.dashboardData?.pan || this.panNumber,
            name: `${store.dashboardData?.firstName || ''} ${store.dashboardData?.lastName || ''}`.trim(),
            status: 'Validated',
            statusCode: 'KV',
            statusDescription: 'KYC verified'
          };
          this.checked = true;
          return;
        }

        // If KYC status is "Not Started", "Pending", or "In Progress", proceed to check KRA
        console.log('KYC Status is:', kycStatus, '- will check KRA status');

        // Step 2: If KYC not done, check KRA status
        console.log('KYC not verified, checking KRA status for PAN:', this.panNumber);
        const kraResponse = await store.getPANKRAStatus({ pan: this.panNumber });
        console.log('KRA response:', kraResponse);

        if (kraResponse.success && kraResponse.data) {
          // Map the KRA response data correctly
          this.kraStatus = {
            panNumber: kraResponse.data.panNumber || store.dashboardData?.pan || this.panNumber,
            name: kraResponse.data.name,
            status: kraResponse.data.status,
            statusCode: kraResponse.data.statusCode,
            statusDescription: kraResponse.data.statusDescription,
            kraStatusCode: kraResponse.data.kraStatusCode
          };
          this.checked = true;
          console.log('KRA Status object created:', this.kraStatus);
          console.log('Is Verified (status === Validated)?', this.kraStatus.status === 'Validated');
        } else {
          this.error = kraResponse.message || "Failed to retrieve KRA status";
        }
      } catch (e) {
        console.error('Status check error:', e);
        this.error = e?.message || "An error occurred while checking status";
      } finally {
        this.loading = false;
        this.isCheckingKRA = false; // Reset lock
      }
    },

    proceed() {
      this.$emit('verified', this.kraStatus);
    },

    proceedToKYC() {
      this.$emit('not-verified', this.kraStatus);
    }
  },
  mounted() {
    // Auto-check on mount if PAN is provided
    if (this.panNumber) {
      this.checkKRAStatus();
    }
  }
};
</script>

<style scoped>
.kra-check-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 20px;
}

.card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 32px;
  max-width: 600px;
  width: 100%;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #AB3498;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.initial-state,
.result-state,
.error-state {
  text-align: center;
}

h2 {
  color: #2c3e50;
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
}

h3 {
  color: #2c3e50;
  margin: 16px 0 12px 0;
  font-size: 20px;
  font-weight: 600;
}

.description {
  color: #666;
  line-height: 1.6;
  margin-bottom: 24px;
}

.icon-check,
.icon-info,
.icon-error {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto 16px;
}

.icon-check {
  background: #d4edda;
  color: #155724;
}

.icon-info {
  background: #d1ecf1;
  color: #0c5460;
}

.icon-error {
  background: #f8d7da;
  color: #721c24;
}

.details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  text-align: left;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item-horizontal {
  display: flex;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
  text-align: left;
  align-items: center;
}

.detail-item-vertical {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
  text-align: left;
}

.status-message-box {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f9ff 100%);
  border-left: 4px solid #0ea5e9;
  border-radius: 8px;
  margin-top: 12px;
  text-align: left;
}

.status-icon {
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.status-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.status-label {
  color: #0284c7;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-text {
  color: #1e293b;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  font-weight: 500;
}

.label {
  color: #666;
  font-weight: 500;
}

.value {
  color: #2c3e50;
  font-weight: 600;
}

.status-verified {
  color: #28a745;
}

.status-pending {
  color: #ffc107;
}

.primary-btn {
  padding: 12px 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background: #AB3498;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s;
  margin-top: 16px;
}

.primary-btn:hover:not(:disabled) {
  background: #8e2a7d;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(171, 52, 152, 0.3);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #AB3498;
}

.secondary-btn {
  padding: 12px 32px;
  border-radius: 8px;
  border: 1px solid #ddd;
  cursor: pointer;
  background: #fff;
  color: #333;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s;
  margin-top: 16px;
}

.secondary-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #bbb;
}

.secondary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.verified {
  animation: fadeIn 0.5s;
}

.not-verified {
  animation: fadeIn 0.5s;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
